import type { ScopeOption } from "@/components/layout/ScopePicker";
import { formatNumber } from "@/lib/format";

/** A picker lists at most this many matches; the footer says when it cut. */
export const PICKER_LIMIT = 50;

/** The leading "All …" row's id. Never a deal number, contract or entity id. */
export const ALL_ID = "__all__";

/** The leading row's id while a search is typed: "Show all N matches". */
export const MATCHES_ID = "__matches__";

/** Case-insensitive substring match over a record's searchable text. */
export function matches(query: string, ...fields: Array<string | null>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => f?.toLowerCase().includes(q));
}

/**
 * A picker's options: the leading "All …" row, then up to `PICKER_LIMIT`
 * matches, plus the footer line when matches were cut.
 *
 * With nothing typed the leading row opens the area's full list ("All deals
 * 571"); with a search typed it opens the list filtered by it ("Show all 12
 * matches"), so the picker scopes the page rather than acting as a global
 * search.
 */
export function pickerOptions(
  query: string,
  matched: ScopeOption[],
  total: number,
  allLabel: string,
): { options: ScopeOption[]; footer: string | undefined } {
  const typed = query.trim().length > 0;
  const lead: ScopeOption[] = typed
    ? matched.length > 0
      ? [{ id: MATCHES_ID, label: `Show all ${formatNumber(matched.length)} matches` }]
      : []
    : [{ id: ALL_ID, label: allLabel, meta: formatNumber(total) }];
  const shown = matched.slice(0, PICKER_LIMIT);
  const footer =
    matched.length > PICKER_LIMIT
      ? `Showing ${PICKER_LIMIT} of ${formatNumber(matched.length)} — keep typing to narrow`
      : undefined;
  return { options: [...lead, ...shown], footer };
}
